import React, { FC, useContext } from 'react';

import { Table } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { moment } from 'client/lib/moment';
import { getOperationLabelsTranslated } from 'client/locale/ru/operations';
import { OperationEvent } from 'client/model/operation';
import { AliceSkill, SmartHome } from 'client/model/skill';
import { sortEventsByDate } from 'client/utils/operations';
import * as keyset from 'i18n/SkillsIndex';

import { PageLayout } from '../Layout/PageLayout';

import styles from './OperationEvents.module.scss';

const t = i18n(keyset);

interface OperationEventsProps {
    skill: AliceSkill | SmartHome;
}

export const AliceAppOperationEvents: FC<OperationEventsProps> = ({ skill }) => {
    const currentAliceAppContext = useContext(CurrentAliceAppContext);
    const operations = currentAliceAppContext?.operations;

    const events: OperationEvent<React.ReactNode>[] | undefined =
        operations
            ?.map((operation) => ({
                skillEntity: {
                    id: skill.id,
                    name: skill.name,
                    channel: skill.channel,
                },
                operation: {
                    ...operation,
                    comment: operation.comment,
                },
            }))
            .sort(sortEventsByDate) || [];

    const columns = [
        { id: 'event', name: t('table-event'), className: styles.aliceAppOperationEvents_cell__event },
        { id: 'time', name: t('table-time'), className: styles.aliceAppOperationEvents_cell__time },
        { id: 'comment', name: t('table-comment'), className: styles.aliceAppOperationEvents_cell__comment },
    ];

    const data = events.map((event) => ({
        event: getOperationLabelsTranslated(event?.skillEntity?.channel)[event?.operation?.type],
        time: moment(event.operation.createdAt).fromNow(),
        comment: event.operation.comment,
    }));

    if (!events?.length) {
        return null;
    }

    return (
        <ErrorBoundary>
            <PageLayout aliceApp={skill} pageTitle={t('events')}>
                <Table className={styles.aliceAppOperationEvents_table} columns={columns} data={data} wordWrap />
            </PageLayout>
        </ErrorBoundary>
    );
};
